import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CalendarX, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Field, Select } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { EditJobDialog } from '@/components/jobs/EditJobDialog';
import { terapkanPola } from '@/components/jobs/PolaNama';
import { formatDateTime, formatDomain, formatRelative } from '@/lib/format';

const FREKUENSI = {
  once: 'Sekali jalan',
  hourly: 'Setiap jam',
  daily: 'Harian',
  weekly: 'Mingguan',
  monthly: 'Bulanan',
  cron: 'Cron',
};

const PENANDA_BAWAAN = {
  hourly: '{h2h}',
  daily: '{d2d}',
  weekly: '{d2d}',
  monthly: '{m2m}',
  cron: '{d2d}_{hh}{MM}',
};

/** Memilih job yang sudah ada untuk dijadwalkan. */
const PilihJobDialog = ({ jobs, onPilih, onClose }) => {
  const [jobId, setJobId] = useState('');
  const belumTerjadwal = jobs.filter((j) => !j.schedule?.enabled);

  return (
    <Modal
      open
      onClose={onClose}
      title="Jadwalkan Job yang Ada"
      description="Ambil konfigurasi job yang sudah jadi, lalu atur jadwalnya."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            disabled={!jobId}
            onClick={() => onPilih(jobs.find((j) => j.id === jobId))}
          >
            Atur Jadwal
          </Button>
        </div>
      }
    >
      {belumTerjadwal.length === 0 ? (
        <p className="py-4 text-sm text-[var(--text-muted)]">
          Semua job Anda sudah punya jadwal. Buat job baru dulu di halaman Buat Scraping.
        </p>
      ) : (
        <Field label="Job" required>
          {(p) => (
            <Select {...p} value={jobId} onChange={(e) => setJobId(e.target.value)}>
              <option value="">Pilih job...</option>
              {belumTerjadwal.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name} ({formatDomain(j.targetUrl)})
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </Modal>
  );
};

/**
 * Layar Scheduler: job yang berjalan sendiri menurut jadwalnya.
 *
 * Penjadwalnya berjalan di dalam proses API dan memindai tiap menit. Kolom
 * "Berikutnya" di bawah bukan perkiraan, melainkan waktu yang benar-benar
 * dipakai penjadwal saat memutuskan menjalankan job.
 */
export const SchedulerPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [memilih, setMemilih] = useState(false);
  const [menyunting, setMenyunting] = useState(null);
  const [hapusJadwal, setHapusJadwal] = useState(null);
  const [hapusJob, setHapusJob] = useState(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['jobs', 'scheduler'],
    queryFn: () => api.get('/jobs', { params: { limit: 100, sort: 'newest' } }),
    // Menyegarkan sendiri supaya "Berikutnya" bergerak tanpa perlu muat ulang.
    refetchInterval: 30_000,
  });

  const semua = data?.data ?? [];
  const terjadwal = semua.filter((j) => j.schedule?.type && j.schedule.type !== 'once');

  const ubahAktif = useMutation({
    mutationFn: ({ job, aktif }) =>
      api.patch(`/jobs/${job.id}`, { schedule: { ...job.schedule, enabled: aktif } }),
    onSuccess: (_, { aktif }) => {
      toast.success(aktif ? 'Jadwal diaktifkan' : 'Jadwal dijeda');
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (err) =>
      toast.error('Gagal mengubah jadwal', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  /**
   * Melepas jadwal tanpa menghapus job-nya.
   *
   * Dipisahkan dari hapus job karena keduanya keputusan yang sangat berbeda:
   * yang satu menghentikan otomatisasi, yang lain membuang konfigurasi
   * scraping beserta riwayatnya. Menyatukannya di satu tombol memastikan
   * suatu saat ada yang kehilangan job hanya karena ingin menjeda jadwal.
   */
  const lepasJadwal = useMutation({
    mutationFn: (job) =>
      api.patch(`/jobs/${job.id}`, {
        schedule: { ...job.schedule, type: 'once', enabled: false },
      }),
    onSuccess: () => {
      toast.success('Jadwal dilepas', { description: 'Job-nya tetap ada di List Job.' });
      setHapusJadwal(null);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (err) => {
      toast.error('Gagal melepas jadwal', { description: err?.message });
      setHapusJadwal(null);
    },
  });

  const hapus = useMutation({
    mutationFn: (job) => api.delete(`/jobs/${job.id}`),
    onSuccess: () => {
      toast.success('Job dihapus');
      setHapusJob(null);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (err) => {
      toast.error('Gagal menghapus', { description: err?.message });
      setHapusJob(null);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Scheduler</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Job yang berjalan sendiri. Penjadwal memindai tiap menit dan menyimpan hasilnya
            ke Penyimpanan Pribadi dengan nama yang ikut berubah menurut waktunya.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={CalendarClock} onClick={() => setMemilih(true)}>
            Jadwalkan Job Ada
          </Button>
          <Link to="/jobs/new">
            <Button icon={Plus}>Buat Job Baru</Button>
          </Link>
        </div>
      </div>

      <Card>
        <CardHeader
          title="Job Berjadwal"
          description={
            terjadwal.length
              ? `${terjadwal.filter((j) => j.schedule.enabled).length} aktif dari ${terjadwal.length} job berjadwal.`
              : undefined
          }
        />

        {isError ? (
          <ErrorState message={error?.message} onRetry={refetch} />
        ) : isLoading ? (
          <TableSkeleton rows={4} columns={6} />
        ) : terjadwal.length === 0 ? (
          <EmptyState
            icon={CalendarClock}
            title="Belum ada job berjadwal"
            description="Ambil job yang sudah ada lalu atur jadwalnya, atau buat job baru dan aktifkan penjadwalan saat membuatnya."
            action={
              <Button variant="secondary" icon={CalendarClock} onClick={() => setMemilih(true)}>
                Jadwalkan Job Ada
              </Button>
            }
          />
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <Tr>
                  <Th>Job</Th>
                  <Th>Frekuensi</Th>
                  <Th>Nama Hasil</Th>
                  <Th>Berikutnya</Th>
                  <Th>Terakhir</Th>
                  <Th>Aksi</Th>
                </Tr>
              </thead>
              <tbody>
                {terjadwal.map((job) => {
                  const pola =
                    job.schedule.namePattern?.trim() ||
                    `${job.name}-${PENANDA_BAWAAN[job.schedule.type] ?? '{d2d}'}`;

                  return (
                    <Tr key={job.id}>
                      <Td>
                        <Link
                          to={`/jobs/${job.id}`}
                          className="font-semibold text-[var(--text-primary)] hover:underline"
                        >
                          {job.name}
                        </Link>
                        <p className="text-xs text-[var(--text-muted)]">
                          {formatDomain(job.targetUrl)}
                        </p>
                      </Td>
                      <Td>
                        <Badge tone={job.schedule.enabled ? 'success' : 'neutral'} size="sm" dot>
                          {FREKUENSI[job.schedule.type] ?? job.schedule.type}
                          {job.schedule.timeOfDay ? ` ${job.schedule.timeOfDay}` : ''}
                        </Badge>
                      </Td>
                      <Td>
                        {job.schedule.autoSave === false ? (
                          <span className="text-xs text-[var(--text-muted)]">tidak disimpan</span>
                        ) : (
                          <code className="font-mono text-xs text-[var(--text-secondary)]">
                            {terapkanPola(pola)}.{job.schedule.outputFormat ?? 'csv'}
                          </code>
                        )}
                      </Td>
                      <Td className="text-[var(--text-secondary)]">
                        {job.schedule.enabled && job.schedule.nextRunAt ? (
                          <>
                            {formatRelative(job.schedule.nextRunAt)}
                            <p className="text-xs text-[var(--text-muted)]">
                              {formatDateTime(job.schedule.nextRunAt)}
                            </p>
                          </>
                        ) : (
                          <span className="text-[var(--text-muted)]">dijeda</span>
                        )}
                      </Td>
                      <Td>
                        {job.stats?.lastRunAt ? (
                          <>
                            <StatusBadge status={job.stats.lastRunStatus ?? 'completed'} size="sm" />
                            <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                              {formatRelative(job.stats.lastRunAt)}
                            </p>
                          </>
                        ) : (
                          <span className="text-xs text-[var(--text-muted)]">belum pernah</span>
                        )}
                      </Td>
                      <Td>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="xs"
                            icon={job.schedule.enabled ? Pause : Play}
                            loading={
                              ubahAktif.isPending && ubahAktif.variables?.job?.id === job.id
                            }
                            onClick={() =>
                              ubahAktif.mutate({ job, aktif: !job.schedule.enabled })
                            }
                          >
                            {job.schedule.enabled ? 'Jeda' : 'Aktifkan'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => setMenyunting(job)}
                          >
                            Atur
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            icon={CalendarX}
                            iconOnly
                            aria-label={`Lepas jadwal ${job.name}`}
                            title="Lepas jadwal, job tetap ada"
                            onClick={() => setHapusJadwal(job)}
                          />
                          <Button
                            variant="ghost"
                            size="xs"
                            icon={Trash2}
                            iconOnly
                            aria-label={`Hapus job ${job.name}`}
                            title="Hapus job seluruhnya"
                            className="hover:text-danger-600"
                            onClick={() => setHapusJob(job)}
                          />
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <Card>
        <CardHeader title="Cara pola nama bekerja" />
        <CardBody className="space-y-2 text-sm text-[var(--text-secondary)]">
          <p>
            Job berjadwal menghasilkan berkas berulang kali. Kalau namanya tetap, penyimpanan
            akan penuh berisi berkas bernama sama dan tidak ada cara membedakan hasil hari ini
            dari hasil kemarin. Penanda di bawah membuat namanya ikut bergerak.
          </p>
          <ul className="space-y-1 font-mono text-xs">
            <li>
              <b>{'{d2d}'}</b> harian: {terapkanPola('{d2d}')}
            </li>
            <li>
              <b>{'{m2m}'}</b> bulanan: {terapkanPola('{m2m}')}
            </li>
            <li>
              <b>{'{h2h}'}</b> per jam: {terapkanPola('{h2h}')}
            </li>
          </ul>
          <p className="text-xs text-[var(--text-muted)]">
            Bagian satuan juga bisa dipakai sendiri: {'{yyyy} {mm} {dd} {hh} {MM}'}. Semuanya
            memakai zona waktu Asia/Jakarta.
          </p>
        </CardBody>
      </Card>

      {memilih && (
        <PilihJobDialog
          jobs={semua}
          onClose={() => setMemilih(false)}
          onPilih={(job) => {
            setMemilih(false);
            setMenyunting(job);
          }}
        />
      )}

      {menyunting && (
        <EditJobDialog job={menyunting} onClose={() => setMenyunting(null)} />
      )}

      <ConfirmDialog
        open={Boolean(hapusJadwal)}
        onClose={() => setHapusJadwal(null)}
        onConfirm={() => lepasJadwal.mutate(hapusJadwal)}
        loading={lepasJadwal.isPending}
        title={`Lepas jadwal "${hapusJadwal?.name}"?`}
        description="Job berhenti berjalan sendiri, tetapi tetap ada di List Job dan masih bisa dijalankan manual."
        confirmLabel="Ya, lepas jadwal"
      />

      <ConfirmDialog
        open={Boolean(hapusJob)}
        onClose={() => setHapusJob(null)}
        onConfirm={() => hapus.mutate(hapusJob)}
        loading={hapus.isPending}
        destructive
        title={`Hapus job "${hapusJob?.name}"?`}
        description="Job, jadwal, dan konfigurasinya dihapus. Data hasil crawl yang sudah tersimpan di Penyimpanan Pribadi tidak ikut terhapus."
        confirmLabel="Ya, hapus job"
      />
    </div>
  );
};
